import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  STATUS_FILTER_OPTIONS,
  findAllSv4Cards,
  formatSv4ListItemCreationDisplay,
  getLoggedInEmpId,
  getStatusInfo,
  getSv4ApproverNameDisplay,
  syncSv4Cards,
} from "./sv4Card.service";

const InfoRow: React.FC<{ label: string; value?: React.ReactNode; hideEmpty?: boolean }> = ({
  label,
  value,
  hideEmpty,
}) => {
  if (hideEmpty && (value === undefined || value === null || value === "")) return null;
  return (
    <div className="d-flex justify-content-between gap-3 py-1">
      <span className="text-muted small">{label}</span>
      <strong className="text-dark text-end small">{value || "-"}</strong>
    </div>
  );
};

const Sv4CardList: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchList = useCallback(async (start: string, end: string, status: string) => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setLoading(true);
    try {
      const data = await findAllSv4Cards({
        empId,
        page: 1,
        limit: 100,
        status,
        startDate: start,
        endDate: end,
      });
      setList(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error(error?.message || "Failed to load SV4 card requests");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList("", "", "");
  }, [fetchList]);

  const handleStatusChange = (val: string) => {
    setStatusFilter(val);
    fetchList(startDate, endDate, val);
  };

  const handleStartDate = (val: string) => {
    if (endDate && val && val > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }
    setStartDate(val);
    if (val && endDate) fetchList(val, endDate, statusFilter);
  };

  const handleEndDate = (val: string) => {
    if (startDate && val && val < startDate) {
      toast.error("End date cannot be before start date.");
      return;
    }
    setEndDate(val);
    if (startDate && val) fetchList(startDate, val, statusFilter);
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    fetchList("", "", statusFilter);
  };

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setSyncing(true);
    try {
      await syncSv4Cards(empId);
      toast.success("Data synced successfully");
      await fetchList(startDate, endDate, statusFilter);
    } catch (error: any) {
      toast.error(error?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openDetails = (item: any) => {
    const ref = item.ReferenceNumber || item.SequenceNumber || item.id;
    if (!ref) {
      toast.error("Reference number not available");
      return;
    }
    navigate(routes.sv4Card.details.replace(":referenceNumber", encodeURIComponent(String(ref))));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">SV4 Card Issue/Re-issue</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">SV4 Card Issue/Re-issue</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <button
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              {syncing ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <i className="ti ti-refresh me-1" />
              )}
              Sync
            </button>
            <Link to={routes.sv4Card.create} className="btn btn-danger">
              <i className="ti ti-plus me-1" />
              Create New Request
            </Link>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="fw-semibold text-dark small d-flex align-items-center">
                <i className="ti ti-filter text-danger me-2 fs-6" />
                Filter Requests
              </span>
              {(startDate || endDate) && (
                <button
                  className="btn btn-sm btn-link text-danger text-decoration-none p-0 small fw-semibold d-flex align-items-center"
                  onClick={clearDateFilter}
                >
                  <i className="ti ti-x me-1" />
                  Clear date filter
                </button>
              )}
            </div>
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1 fw-medium">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  max={endDate || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => handleStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1 fw-medium">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  min={startDate || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => handleEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label text-muted small mb-1 fw-medium">Status</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
            <p className="text-muted mt-2 mb-0">Loading...</p>
          </div>
        ) : list.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">No request available</div>
          </div>
        ) : (
          <div className="row g-3">
            {list.map((item: any, index: number) => {
              const statusCode = item.RequestStatus ?? "";
              const statusInfo = getStatusInfo(statusCode);
              return (
                <div className="col-xl-6" key={`${item.SequenceNumber || item.ReferenceNumber || index}`}>
                  <div className="card border shadow-sm h-100 overflow-hidden">
                    <div
                      className="px-3 py-2 d-flex align-items-center gap-2 text-white"
                      style={{ backgroundColor: statusInfo.header }}
                    >
                      <span className="fw-semibold">Sequence :</span>
                      <span className="fw-bold">{item.SequenceNumber || "-"}</span>
                    </div>
                    <div className="card-body">
                      <InfoRow label="Personal No" value={item.EmpID} hideEmpty />
                      <InfoRow label="Employee Name" value={item.EmployeeName} hideEmpty />
                      <InfoRow label="Designation" value={item.Designation} hideEmpty />
                      <InfoRow label="Department" value={item.Department} hideEmpty />
                      <InfoRow label="Place of Posting" value={item.PlaceOfPosting} hideEmpty />
                      <InfoRow label="Contact No" value={item.ContactNumber} hideEmpty />
                      <InfoRow
                        label="SV4 Creation Date"
                        value={formatSv4ListItemCreationDisplay(item)}
                      />
                      <InfoRow label="Approver User ID" value={item.ApproverUserID} hideEmpty />
                      <InfoRow
                        label="Approver Name"
                        value={getSv4ApproverNameDisplay(item, "-")}
                      />
                      <InfoRow
                        label="Approver Remarks"
                        value={item.HRemarks?.trim() ? item.HRemarks : "-"}
                      />
                      <div className="d-flex justify-content-between gap-3 py-1">
                        <span className="text-muted small">Request Status</span>
                        <strong className="text-end small" style={{ color: statusInfo.text }}>
                          {statusInfo.label}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-end mt-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openDetails(item)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sv4CardList;
